import {
  getExpansionDashboard,
  getExpansionLookup,
  getExpansionSelect,
  getExpansionSignalTypes,
  getExpansionRecent,
  getExpansionWideExpanders,
  getExpansionSavedListActivity,
  getSavedSearchTypes,
} from "@/api/expansions";
import {
  getSaveList,
  addSaveList,
  updateSaveList,
  deleteSaveList,
} from "@/api/company";
import { MAP_MODE, MAX_VISIBLE_MARKETS, MAP_FLOWS_REQUEST_SIZE, TOP_FLOWS_RETRY_SIZE, flowsSizeForDensity } from "@/views/home/components/Expansion/shared/taxonomy";
import { DEFAULT_WINDOW_DAYS } from "@/views/home/components/Expansion/shared/dateRange";

export const COMPANY_LIST_PER_PAGE = 25;
// Floor for a max-only employee range; mirrors backend EMPLOYEE_RANGE_FLOOR.
const EMPLOYEE_RANGE_FLOOR = 0;

// Build the date params payload the backend expects. Returns an empty
// object when no window is set — preserves current "all time" behavior
// so the picker is non-destructive even before backend ships support.
function dateRangeParams(range) {
  if (!range) return {};
  const out = {};
  if (range.from) out.date_from = range.from;
  if (range.to) out.date_to = range.to;
  if (range.window_days) out.window_days = range.window_days;
  return out;
}

// Job / Advertisement / News source filters → forwarded to the expansion backend
// (buildCompanyProfileFilters EXISTS clauses). Same wire keys as company search so the
// reused filter components serialize identically. Omitted when empty (undefined).
function sourceFilterParams(rail) {
  const r = rail || {};
  const a = (v) => (Array.isArray(v) && v.length ? v : undefined);
  return {
    job_titles: a(r.job_titles), job_locations: a(r.job_locations),
    job_exclude_locations: a(r.job_exclude_locations), job_posted_dates: a(r.job_posted_dates),
    advertisement_target_locations: a(r.advertisement_target_locations),
    advertisement_exclude_target_locations: a(r.advertisement_exclude_target_locations),
    advertisement_search_terms: a(r.advertisement_search_terms),
    advertisement_start_dates: a(r.advertisement_start_dates),
    advertisement_end_dates: a(r.advertisement_end_dates),
    news_categories: a(r.news_categories), news_published_dates: a(r.news_published_dates),
    keywords: a(r.keywords),
    // AND/OR operators for the source filters (backend reads body.filter_conditions).
    filter_conditions: a(r.filter_conditions),
  };
}

// Rail filters → API in company-search canonical names (employees/founded_dates/revenues); origins sent per-builder as locations.
function railEmployeesParam(rail) {
  const r = rail || {};
  const ranges = [];
  for (const s of (Array.isArray(r.employee_slugs) ? r.employee_slugs : [])) {
    if (typeof s !== "string") continue;
    const [a, b] = s.split("-");
    const lo = Number(a);
    if (!Number.isFinite(lo)) continue;
    if (b == null || b === "") { ranges.push([lo]); continue; }
    const hi = Number(b);
    if (Number.isFinite(hi)) ranges.push([lo, hi]);
  }
  // Number(null)===0 — guard unset bounds, else an empty rail emits [[0,0]] ("exactly 0 employees").
  const lo = (r.employee_min == null || r.employee_min === "") ? NaN : Number(r.employee_min);
  const hi = (r.employee_max == null || r.employee_max === "") ? NaN : Number(r.employee_max);
  if (Number.isFinite(lo) && Number.isFinite(hi)) ranges.push([lo, hi]);
  else if (Number.isFinite(lo)) ranges.push([lo]);
  else if (Number.isFinite(hi)) ranges.push([EMPLOYEE_RANGE_FLOOR, hi]);
  return ranges.length ? ranges : undefined;
}

function rangePairParam(min, max) {
  const lo = (min == null || min === "") ? null : Number(min);
  const hi = (max == null || max === "") ? null : Number(max);
  if (lo == null && hi == null) return undefined;
  return [lo, hi];
}

function railFilterParams(rail) {
  const r = rail || {};
  const a = (v) => (Array.isArray(v) && v.length ? v : undefined);
  return {
    directions: a(r.directions), freshness: a(r.freshness), scopes: a(r.scopes),
    ahead_of_pace: r.ahead_of_pace === true ? true : undefined,
    vertical_categories: a(r.vertical_categories), vertical_sub_categories: a(r.vertical_sub_categories),
    verticals: a(r.verticals), technologies: a(r.technologies),
    companies: a(r.companies), company_groups: a(r.company_groups),
    employees: railEmployeesParam(r),
    founded_dates: rangePairParam(r.founded_min, r.founded_max),
    revenues: rangePairParam(r.revenue_min, r.revenue_max),
    only_contraction: r.only_contraction === true ? true : undefined,
    signal_strengths: a(r.signal_strengths), signal_type_slugs: a(r.signal_type_slugs),
  };
}

// Pre-rename saved_searches rows used legacy keys; map to /search's wire
// format on restore. company_name_query has no UUID equivalent — drop.
function migrateLegacyRailFilters(rail) {
  if (!rail || typeof rail !== "object") return rail;
  const out = { ...rail };
  const promote = (legacy_key, new_key) => {
    if (out[legacy_key] !== undefined && out[new_key] === undefined) {
      out[new_key] = out[legacy_key];
    }
    delete out[legacy_key];
  };
  promote("industry_category_ids",     "vertical_categories");
  promote("industry_sub_category_ids", "vertical_sub_categories");
  promote("vertical_ids",              "verticals");
  promote("technology_ids",            "technologies");
  if (out.company_name_query !== undefined) delete out.company_name_query;
  return out;
}

// Empty-shape factory. Wire-format names mirror /search.
function defaultRailFilters() {
  return {
    directions: [], freshness: [], scopes: [], ahead_of_pace: false, origins: [],
    vertical_categories: [], vertical_sub_categories: [],
    verticals: [], technologies: [],
    companies: [],
    // Saved-list scope — populated when the user clicks a row in the For-you
    // panel. Backend filters es.domain_search_id by Profile_Companies.group_id.
    company_groups: [],
    employee_slugs: [], employee_min: null, employee_max: null,
    founded_min: null, founded_max: null,
    revenue_min: null, revenue_max: null,
    only_contraction: false,
    signal_strengths: [],
    signal_type_slugs: [],
    // Job / Advertisement / News / Keyword source filters + their AND/OR operators.
    job_titles: [], job_locations: [], job_exclude_locations: [], job_posted_dates: [],
    advertisement_target_locations: [], advertisement_exclude_target_locations: [],
    advertisement_search_terms: [], advertisement_start_dates: [], advertisement_end_dates: [],
    news_categories: [], news_published_dates: [], keywords: [],
    filter_conditions: [],
  };
}

// Source-filter array keys — shared by railSnapshot so a source-only change bumps the
// stale-fetch guard (otherwise a slow source-filtered fetch could overwrite a newer one).
const RAIL_SOURCE_KEYS = [
  "job_titles", "job_locations", "job_exclude_locations", "job_posted_dates",
  "advertisement_target_locations", "advertisement_exclude_target_locations",
  "advertisement_search_terms", "advertisement_start_dates", "advertisement_end_dates",
  "news_categories", "news_published_dates", "keywords",
];

// Race-guard snapshot. Every defaultRailFilters() field MUST appear here.
function railSnapshot(rail) {
  if (!rail) return "";
  const arr = (xs) => (Array.isArray(xs) ? [...xs].sort().join(",") : "");
  const n = (v) => (v == null ? "" : String(v));
  // Date sub-fields are positional [start,end] — join unsorted to preserve order.
  const ord = (xs) => (Array.isArray(xs) ? xs.join(",") : "");
  const fc = (Array.isArray(rail.filter_conditions) ? rail.filter_conditions : [])
    .map((c) => (c ? `${c.key}:${c.operator}` : "")).sort().join(",");
  return [
    arr(rail.directions),
    arr(rail.freshness),
    arr(rail.scopes),
    rail.ahead_of_pace === true ? "1" : "0",
    arr(rail.origins),
    arr(rail.vertical_categories),
    arr(rail.vertical_sub_categories),
    arr(rail.verticals),
    arr(rail.technologies),
    arr(rail.companies),
    arr(rail.company_groups),
    arr(rail.employee_slugs),
    n(rail.employee_min),
    n(rail.employee_max),
    n(rail.founded_min),
    n(rail.founded_max),
    n(rail.revenue_min),
    n(rail.revenue_max),
    rail.only_contraction === true ? "1" : "0",
    arr(rail.signal_strengths),
    arr(rail.signal_type_slugs),
    ...RAIL_SOURCE_KEYS.map((k) => (k.endsWith("_dates") ? ord(rail[k]) : arr(rail[k]))),
    fc,
  ].join("|");
}

// In-flight promise cache for the saved-search-type id lookup. fetchSavedViews
// and createSavedView both resolve the id on first call; without this guard,
// a quick "+ Save" after mount fires two concurrent GETs to /saved_search_types.
let _saved_search_type_id_promise = null;

const state = {
  dashboard: null,
  // Snapshot of dashboard.data from the most recent UNSCOPED fetch (no country_codes filter). Drives the chip row so it stays rich when multi-select narrows `dashboard` itself.
  dashboard_market_index: null,
  country_detail: null,
  signal_types_catalog: {},
  // Export field picker options (company firmographics + signal columns). Cached
  // once per session like the search export's companyExportFields.
  export_fields: [],
  // Cross-market recent stage transitions. Drives the "Just moved" panel
  // in global view (where country_detail isn't loaded). Populated by
  // fetchRecentMovers via /expansions/dashboard/recent — enriched on the
  // backend with company_name + logo_url + home_country_code so each row
  // renders without N+1 lookups.
  recent_movers: [],
  recent_movers_loading: false,

  // What's-new "Fast movers" tab: ahead_of_pace=true companies (depth signal).
  fast_movers: [],
  fast_movers_loading: false,

  // What's-new "Wide expanders" tab: companies that hit ≥3 distinct markets
  // in the current window (breadth signal). Different commercial intent from
  // Fast movers — aggressive multi-market push, not single-corridor speed.
  wide_expanders: [],
  wide_expanders_loading: false,

  // For-you tab: caller's saved company lists, filtered to lists whose
  // members had stage transitions in the current window. Replaces the old
  // "all saved lists chronologically" view, which was just settings noise.
  saved_list_activity: [],
  saved_list_activity_loading: false,

  selected_country: null,
  // Multi-country selection. null = not in multi mode (single OR global, see
  // selected_country). Array of CCA2 codes = multi-market scope. Mutually
  // exclusive with selected_country at the action level (setSelectedCountries
  // clears selected_country; SET_SELECTED_COUNTRY clears the list).
  selected_country_list: null,
  selected_stage_slug: null,
  map_mode: MAP_MODE.GLOBAL,
  // Flow direction filter, orthogonal to selection scope. 'all' = both inbound + outbound.
  // 'inbound' = companies arriving in scoped markets. 'outbound' = companies leaving scoped markets.
  flow_direction: "all",

  // Server-side rail filters. Shape lives in defaultRailFilters().
  rail_filters: defaultRailFilters(),

  client_added_country_codes: [],

  // Saved Views — reuses /search's saved-search backend. `search_type_id` is
  // resolved at runtime from /saved_search_types (slug='expansion'). `views`
  // is the user's saved list; `active_view_id` is the one currently applied,
  // null = "All / unsaved". `parameters` payload = whatever's needed to
  // restore the view (country + stage + rail_filters + date_range + rail
  // pinned-section list).
  saved_views: {
    search_type_id: null,
    views: [],
    active_view_id: null,
    loading: false,
    // Flipped to true after the first fetch (success OR failure) so a broken
    // env (missing migration, network error) doesn't retry on every remount.
    // Reset by RESET; bypassed by { force: true }.
    fetch_attempted: false,
  },

  // Date window for all aggregates. Default matches the field most of the
  // dashboard surfaces (new_transitions_30d). Null window = "all time".
  date_range: { from: null, to: null, window_days: DEFAULT_WINDOW_DAYS },

  company_page: 1,
  company_per_page: COMPANY_LIST_PER_PAGE,

  loading: {
    dashboard: false,
    country_detail: false,
    signal_types: false,
  },

  // In-flight request counters — incremented on fetch start, decremented on
  // end. The loading.* booleans are derived as counter > 0 so overlapping
  // fetches (rapid filter switches) don't toggle loading off prematurely
  // when the first fetch completes while a second is still in flight.
  loading_counter: {
    dashboard: 0,
    country_detail: 0,
  },

  error: {
    dashboard: null,
    country_detail: null,
  },
};

const getters = {
  visibleCountryChips(state) {
    if (!state.dashboard) return [];
    // User picks (single + multi) are always-keep — they bypass the MAX_VISIBLE_MARKETS cap so the chip row never hides the user's own selection behind "+N more".
    // The cap still limits filler chips (watchlist + added + top).
    const selected = state.selected_country ? [state.selected_country] : [];
    const multi    = Array.isArray(state.selected_country_list) ? state.selected_country_list : [];
    const watchlist = (state.dashboard.watchlist_markets || []).map((row) => row.country_code);
    const added = state.client_added_country_codes;
    const market_source = state.dashboard_market_index || state.dashboard;
    const top = (market_source?.data || []).map((row) => row.country_code);

    const seen = new Set();
    const out = [];
    // Pass 1: always-keep — every user pick, no cap.
    [...selected, ...multi].forEach((code) => {
      if (!code || seen.has(code)) return;
      seen.add(code);
      out.push(code);
    });
    // Pass 2: filler chips up to MAX_VISIBLE_MARKETS total.
    [...watchlist, ...added, ...top].forEach((code) => {
      if (!code || seen.has(code)) return;
      if (out.length >= MAX_VISIBLE_MARKETS) return;
      seen.add(code);
      out.push(code);
    });
    return out;
  },

  totalAvailableMarkets(state) {
    // Read from the unscoped index so "+N more" reflects all markets, not just the multi-selected subset.
    const source = state.dashboard_market_index || state.dashboard;
    if (!source) return 0;
    return (source.data || []).length;
  },

  defaultCountryCode(state, getters) {
    if (state.selected_country) return state.selected_country;
    const list = getters.visibleCountryChips;
    return list[0] || null;
  },

  topCountryByActivity(state) {
    if (!state.dashboard || !state.dashboard.data || !state.dashboard.data.length) return null;
    return state.dashboard.data[0];
  },

  // Two modes:
  //   SELECTED — country picked → all 4 tiles describe that country.
  //   GLOBAL   — no country → tile 1 = movement metric; tiles 2-4 use
  //              hybrid score (count × √rate) and skip top-3 by volume
  //              so next-tier markets get the spotlight (otherwise
  //              USA → India → India → India dominates).
  kpiSummary(state) {
    const data = (state.dashboard && state.dashboard.data) || [];
    const selected_row = state.selected_country
      ? data.find((r) => r.country_code === state.selected_country)
      : null;

    // Picked-country path. If the row is missing from `data` (window filtered
    // it out, or backend dropped it from the top-N), keep tiles in SELECTED
    // mode with the country chrome but null metric rows — the UI surfaces a
    // "no signals yet in this window" message rather than silently flipping
    // back to GLOBAL with India in tile 2.
    if (state.selected_country) {
      if (!selected_row) {
        const stub = { country_code: state.selected_country, total: 0, count: 0, rate: null, active_markets: data.length };
        return {
          is_user_selected: true,
          no_data_in_window: true,
          tile1_focus: stub,
          tile1_global: null,
          best_to_contact: null,
          moving_fast: null,
          new_activity: null,
        };
      }
      const total = Number(selected_row.total_companies_with_signals) || 1;
      const tile = (count) => kpiRow(selected_row, data.length, count, count / total);
      return {
        is_user_selected: true,
        no_data_in_window: false,
        tile1_focus: kpiRow(selected_row, data.length),
        tile1_global: null,
        best_to_contact: tile(Number(selected_row.landing_count) || 0),
        moving_fast: tile(Number(selected_row.pairs_ahead_of_pace_30d) || 0),
        new_activity: tile(Number(selected_row.new_transitions_30d) || 0),
      };
    }

    // GLOBAL — one pass collects: top-3 by volume (to exclude), running
    // best-by-score per metric, and the global movement aggregates.
    // Hybrid score = count × √rate. Singapore (200 × √0.25 = 100) beats
    // Maldives (18 × √0.56 = 13.5) without a separate volume floor.
    const top_volume = []; // ascending-sorted, kept at length ≤ 3
    let total_new = 0;
    let active = 0;
    const scored = data.map((r) => {
      const total = Number(r.total_companies_with_signals) || 1;
      const fresh_n = Number(r.new_transitions_30d) || 0;
      total_new += fresh_n;
      if (fresh_n > 0) active += 1;
      insertTop(top_volume, r, total, 3);
      return { row: r, total, fresh_n };
    });
    const exclude_set = new Set(top_volume.map((r) => r.country_code));

    const bestByScore = (key) => {
      let best = null;
      for (const { row, total } of scored) {
        if (exclude_set.has(row.country_code)) continue;
        const count = Number(row[key]) || 0;
        if (count <= 0) continue;
        const rate = count / total;
        const score = count * Math.sqrt(rate);
        if (!best || score > best.score) best = { row, count, rate, score };
      }
      return best;
    };

    const landing = bestByScore("landing_count");
    const pace = bestByScore("pairs_ahead_of_pace_30d");
    const fresh = bestByScore("new_transitions_30d");

    return {
      is_user_selected: false,
      tile1_focus: null,
      tile1_global: { new_transitions: total_new, active_markets: active, total_markets: data.length },
      best_to_contact: landing && kpiRow(landing.row, data.length, landing.count, landing.rate),
      moving_fast: pace && kpiRow(pace.row, data.length, pace.count, pace.rate),
      new_activity: fresh && kpiRow(fresh.row, data.length, fresh.count, fresh.rate),
    };
  },
};

// Maintain a length-N min-heap-ish list of rows with the highest `total`.
// O(N) per insert with N=3 — cheaper than sorting all rows just for top-3.
function insertTop(list, row, total, cap) {
  if (list.length < cap) {
    list.push(row);
    list.sort((a, b) => (Number(a.total_companies_with_signals) || 0) - (Number(b.total_companies_with_signals) || 0));
    return;
  }
  const smallest = Number(list[0].total_companies_with_signals) || 0;
  if (total <= smallest) return;
  list[0] = row;
  list.sort((a, b) => (Number(a.total_companies_with_signals) || 0) - (Number(b.total_companies_with_signals) || 0));
}

// Tile 1 only needs country + total; tiles 2-4 need the metric value + rate.
function kpiRow(row, active_markets, count = 0, rate = null) {
  if (!row) return null;
  return {
    country_code: row.country_code,
    total: Number(row.total_companies_with_signals) || 0,
    count: Number(count) || 0,
    rate,
    active_markets,
  };
}

const mutations = {
  SET_DASHBOARD(state, payload) { state.dashboard = payload; },
  SET_DASHBOARD_MARKET_INDEX(state, payload) { state.dashboard_market_index = payload; },
  SET_FLOW_DIRECTION(state, payload) {
    state.flow_direction = (payload == "inbound" || payload == "outbound") ? payload : "all";
  },
  SET_COUNTRY_DETAIL(state, payload) { state.country_detail = payload; },
  SET_SIGNAL_TYPES(state, payload) { state.signal_types_catalog = payload || {}; },
  SET_EXPORT_FIELDS(state, payload) { state.export_fields = Array.isArray(payload) ? payload : []; },
  SET_RECENT_MOVERS(state, payload) { state.recent_movers = Array.isArray(payload) ? payload : []; },
  SET_RECENT_MOVERS_LOADING(state, payload) { state.recent_movers_loading = !!payload; },
  SET_FAST_MOVERS(state, payload) { state.fast_movers = Array.isArray(payload) ? payload : []; },
  SET_FAST_MOVERS_LOADING(state, payload) { state.fast_movers_loading = !!payload; },
  SET_WIDE_EXPANDERS(state, payload) { state.wide_expanders = Array.isArray(payload) ? payload : []; },
  SET_WIDE_EXPANDERS_LOADING(state, payload) { state.wide_expanders_loading = !!payload; },
  SET_SAVED_LIST_ACTIVITY(state, payload) { state.saved_list_activity = Array.isArray(payload) ? payload : []; },
  SET_SAVED_LIST_ACTIVITY_LOADING(state, payload) { state.saved_list_activity_loading = !!payload; },

  SET_SELECTED_COUNTRY(state, code) {
    state.selected_country = code;
    // Single-country mode wins — clear any multi selection so they don't fight.
    if (code != null) state.selected_country_list = null;
  },
  SET_SELECTED_COUNTRY_LIST(state, codes) {
    // null / [] / non-array → clear multi mode
    const cleaned = Array.isArray(codes)
      ? [...new Set(codes.filter(Boolean).map((c) => String(c).toUpperCase()))]
      : null;
    state.selected_country_list = (cleaned && cleaned.length > 0) ? cleaned : null;
    // Mirror single-country slot for legacy consumers — derived state, not authoritative.
    // selected_country_list is the single source of truth; selected_country is a 1-item alias.
    state.selected_country = (cleaned && cleaned.length === 1) ? cleaned[0] : null;
  },
  SET_SELECTED_STAGE(state, slug) { state.selected_stage_slug = slug; },
  SET_MAP_MODE(state, mode) { state.map_mode = mode; },
  SET_RAIL_FILTERS(state, filters) {
    const arr = (v) => (Array.isArray(v) ? v : []);
    const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
    state.rail_filters = {
      directions: arr(filters?.directions),
      freshness:  arr(filters?.freshness),
      scopes:     arr(filters?.scopes),
      ahead_of_pace: filters?.ahead_of_pace === true,
      origins:    arr(filters?.origins),
      vertical_categories:     arr(filters?.vertical_categories),
      vertical_sub_categories: arr(filters?.vertical_sub_categories),
      verticals:    arr(filters?.verticals),
      technologies: arr(filters?.technologies),
      companies:    arr(filters?.companies),
      company_groups: arr(filters?.company_groups),
      employee_slugs: arr(filters?.employee_slugs),
      employee_min:   num(filters?.employee_min),
      employee_max:   num(filters?.employee_max),
      founded_min:    num(filters?.founded_min),
      founded_max:    num(filters?.founded_max),
      revenue_min:    num(filters?.revenue_min),
      revenue_max:    num(filters?.revenue_max),
      only_contraction: filters?.only_contraction === true,
      signal_strengths: arr(filters?.signal_strengths),
      signal_type_slugs: arr(filters?.signal_type_slugs),
      // Source filters (Jobs/Ads/News) — arr() keeps order; date sub-fields are positional [start,end].
      job_titles:                             arr(filters?.job_titles),
      job_locations:                          arr(filters?.job_locations),
      job_exclude_locations:                  arr(filters?.job_exclude_locations),
      job_posted_dates:                       arr(filters?.job_posted_dates),
      advertisement_target_locations:         arr(filters?.advertisement_target_locations),
      advertisement_exclude_target_locations: arr(filters?.advertisement_exclude_target_locations),
      advertisement_search_terms:             arr(filters?.advertisement_search_terms),
      advertisement_start_dates:              arr(filters?.advertisement_start_dates),
      advertisement_end_dates:                arr(filters?.advertisement_end_dates),
      news_categories:                        arr(filters?.news_categories),
      news_published_dates:                   arr(filters?.news_published_dates),
      keywords:                               arr(filters?.keywords),
      // AND/OR operators [{key, operator}] for the source filters.
      filter_conditions:                      arr(filters?.filter_conditions),
    };
  },
  SET_DATE_RANGE(state, { from, to, window_days }) {
    state.date_range = {
      from: from || null,
      to: to || null,
      window_days: Number(window_days) || null,
    };
  },

  ADD_CLIENT_COUNTRY(state, code) {
    if (!code) return;
    if (state.client_added_country_codes.includes(code)) return;
    state.client_added_country_codes = [...state.client_added_country_codes, code];
  },
  REMOVE_CLIENT_COUNTRY(state, code) {
    state.client_added_country_codes = state.client_added_country_codes.filter((c) => c !== code);
  },

  SET_COMPANY_PAGE(state, page) { state.company_page = page; },
  SET_COMPANY_PER_PAGE(state, n) { state.company_per_page = n; },

  // In-place key writes — state.loading/state.error are initialized with all
  // keys so Vue tracks them. Avoids a fresh object allocation + spurious
  // re-renders on every fetch tick.
  SET_LOADING(state, { key, value }) { state.loading[key] = value; },
  INC_LOADING(state, key) {
    if (state.loading_counter[key] == null) state.loading_counter[key] = 0;
    state.loading_counter[key]++;
    state.loading[key] = state.loading_counter[key] > 0;
  },
  DEC_LOADING(state, key) {
    if (state.loading_counter[key] == null) state.loading_counter[key] = 0;
    state.loading_counter[key] = Math.max(0, state.loading_counter[key] - 1);
    state.loading[key] = state.loading_counter[key] > 0;
  },
  SET_ERROR(state, { key, value }) { state.error[key] = value; },

  // Saved views.
  SET_SAVED_VIEWS_TYPE_ID(state, id) { state.saved_views.search_type_id = id; },
  SET_SAVED_VIEWS(state, list) { state.saved_views.views = Array.isArray(list) ? list : []; },
  SET_ACTIVE_VIEW_ID(state, id) { state.saved_views.active_view_id = id || null; },
  SET_SAVED_VIEWS_LOADING(state, v) { state.saved_views.loading = v === true; },
  SET_SAVED_VIEWS_FETCH_ATTEMPTED(state, v) { state.saved_views.fetch_attempted = v === true; },
  UPSERT_SAVED_VIEW(state, view) {
    if (!view || view.saved_search_id == null) return;
    const i = state.saved_views.views.findIndex((v) => v.saved_search_id === view.saved_search_id);
    if (i >= 0) {
      state.saved_views.views = state.saved_views.views.map((v, idx) => (idx === i ? { ...v, ...view } : v));
    } else {
      state.saved_views.views = [view, ...state.saved_views.views];
    }
  },
  REMOVE_SAVED_VIEW(state, id) {
    state.saved_views.views = state.saved_views.views.filter((v) => v.saved_search_id !== id);
    if (state.saved_views.active_view_id === id) state.saved_views.active_view_id = null;
  },

  RESET(state) {
    state.dashboard = null;
    state.dashboard_market_index = null;
    state.country_detail = null;
    state.selected_country = null;
    state.selected_country_list = null;
    state.selected_stage_slug = null;
    state.flow_direction = "all";
    state.client_added_country_codes = [];
    state.company_page = 1;
    state.rail_filters = defaultRailFilters();
    state.saved_views = { search_type_id: state.saved_views?.search_type_id || null, views: [], active_view_id: null, loading: false, fetch_attempted: false };
  },
};

const actions = {
  async fetchDashboard({ commit, state, rootState }, { force = false, density_n } = {}) {
    commit("INC_LOADING", "dashboard");
    commit("SET_ERROR", { key: "dashboard", value: null });
    try {
      const profile_id = rootState.user?.currentId?.profile_id;
      // Don't fire the POST before auth hydrates — a missing profile_id 400s (mirrors company-search's pre-request guard).
      if (!profile_id) { commit("DEC_LOADING", "dashboard"); return null; }
      const rail = state.rail_filters || {};
      const buildBody = (flows_size) => ({
        profile_id,
        // Multi-country scope — undefined when not in multi mode so the
        // backend falls through to global aggregates.
        country_codes: (Array.isArray(state.selected_country_list) && state.selected_country_list.length)
          ? state.selected_country_list
          : undefined,
        // Flow direction — backend slices each query by inbound/outbound/all.
        direction: state.flow_direction || "all",
        flows_size,
        // Sidebar filters mirror fetchCountryDetail's payload so the map chip
        // count matches the company list count — without these, the dashboard
        // returns unfiltered counts (e.g. 266) while the list shows the
        // filtered count (258). Same field names as /lookup.
        stage_slug: state.selected_stage_slug || undefined,
        ...railFilterParams(rail),
        ...sourceFilterParams(rail),
        ...dateRangeParams(state.date_range),
      });
      // Size top_flows for the unified map. The backend silently clamps flows_size
      // to the plan cap (never 416), so we request the size the density needs and
      // let the server truncate. Default to the first-paint size; if a caller passes
      // density_n (slider crossed past DEFAULT and arcs would otherwise vanish), upsize.
      const initial_flows_size = density_n
        ? Math.max(MAP_FLOWS_REQUEST_SIZE, flowsSizeForDensity(density_n))
        : MAP_FLOWS_REQUEST_SIZE;
      const res = await getExpansionDashboard(buildBody(initial_flows_size));
      commit("SET_DASHBOARD", res);
      // Only refresh the unscoped market list on global fetches — multi-select fetches return a narrowed dashboard.data we don't want to overwrite the chip-row source with.
      const isMultiFetch = Array.isArray(state.selected_country_list) && state.selected_country_list.length;
      if (!isMultiFetch) {
        commit("SET_DASHBOARD_MARKET_INDEX", res);
      } else if (!state.dashboard_market_index) {
        // Deep-link / saved-view restore landed straight in multi mode → market_index would stay null for the entire session and degrade the chip row to just the picks. Fire one background unscoped fetch to seed it. Fire-and-forget; errors are non-blocking.
        getExpansionDashboard({ profile_id, flows_size: TOP_FLOWS_RETRY_SIZE, ...dateRangeParams(state.date_range) })
          .then((unscoped) => { if (unscoped) commit("SET_DASHBOARD_MARKET_INDEX", unscoped); })
          .catch(() => { /* non-critical — chip row degrades gracefully */ });
      }
      return res;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return null;
      commit("SET_ERROR", { key: "dashboard", value: err?.message || "load_failed" });
      throw err;
    } finally {
      commit("DEC_LOADING", "dashboard");
    }
  },

  async fetchCountryDetail({ commit, state, rootState }, { country_code, page, per_page, stage_slug, home_country_code } = {}) {
    // null country_code = global aggregate; threaded through the race guard
    // so two consecutive global fetches don't stomp on each other.
    const code = country_code !== undefined ? country_code : state.selected_country;
    const requested_country = code || null;
    const requested_stage = (stage_slug === undefined ? state.selected_stage_slug : stage_slug) || null;
    const requested_rail_snapshot = railSnapshot(state.rail_filters);
    commit("INC_LOADING", "country_detail");
    commit("SET_ERROR", { key: "country_detail", value: null });
    try {
      const profile_id = rootState.user?.currentId?.profile_id;
      const rail = state.rail_filters || {};
      const arr_or_undef = (v) => (Array.isArray(v) && v.length ? v : undefined);
      // Explicit `home_country_code` arg wins over rail.origins[].
      const rail_origins_payload = arr_or_undef(rail.origins);
      // Multi-country mode wins over single + global. When country_codes is
      // present the backend filters via IN; country_code + is_global are ignored.
      const multi_codes = (Array.isArray(state.selected_country_list) && state.selected_country_list.length)
        ? state.selected_country_list
        : undefined;
      const res = await getExpansionLookup({
        profile_id,
        country_codes: multi_codes,
        direction: state.flow_direction || "all",
        // Backend requires an explicit is_global flag when country_code AND
        // country_codes are both omitted; a bare missing pair rejects with
        // DATA_MISSING.
        country_code: multi_codes ? undefined : (requested_country || undefined),
        is_global: multi_codes ? undefined : (requested_country ? undefined : true),
        locations: home_country_code !== undefined ? [home_country_code] : rail_origins_payload,
        page: page || state.company_page,
        per_page: per_page || state.company_per_page,
        stage_slug: requested_stage || undefined,
        ...railFilterParams(rail),
        ...sourceFilterParams(rail),
        ...dateRangeParams(state.date_range),
      });
      // Drop stale responses (country OR stage OR rail filter changed
      // mid-flight). KPI tile clicks fire 3 fetches in quick succession
      // (selectCountry, selectStage, applyRailFilters) and only the last
      // carries the right rail filter — without the rail check, a slow
      // first fetch could overwrite the filtered list with unfiltered data.
      if (
        requested_country !== (state.selected_country || null)
        || requested_stage !== (state.selected_stage_slug || null)
        || requested_rail_snapshot !== railSnapshot(state.rail_filters)
      ) return null;
      commit("SET_COUNTRY_DETAIL", res);
      return res;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return null;
      commit("SET_ERROR", { key: "country_detail", value: err?.message || "load_failed" });
      throw err;
    } finally {
      // Counter handles overlapping fetches — every INC pairs with a DEC.
      // The old code wrapped DEC in a stale-request guard, which left loading
      // stuck on "true" when a stale fetch finished after a newer one (DEC
      // skipped, counter never returned to 0). User-flagged: "the map
      // sometimes is missing the loading animation".
      commit("DEC_LOADING", "country_detail");
    }
  },

  async fetchCompanyIds({ state, rootState }, { select_size, home_country_code } = {}) {
    // Mirrors fetchCountryDetail's filter payload so "Select all N" picks
    // from the same pool the visible list shows.
    const profile_id = rootState.user?.currentId?.profile_id;
    const rail = state.rail_filters || {};
    const arr_or_undef = (v) => (Array.isArray(v) && v.length ? v : undefined);
    const rail_origins_payload = arr_or_undef(rail.origins);
    const multi_codes = (Array.isArray(state.selected_country_list) && state.selected_country_list.length)
      ? state.selected_country_list
      : undefined;
    const requested_country = state.selected_country || null;
    const res = await getExpansionSelect({
      profile_id,
      country_codes: multi_codes,
      direction: state.flow_direction || "all",
      country_code: multi_codes ? undefined : (requested_country || undefined),
      is_global: multi_codes ? undefined : (requested_country ? undefined : true),
      stage_slug: state.selected_stage_slug || undefined,
      // company_groups rides in railFilterParams so "Select all N" hits the same cohort.
      locations: home_country_code !== undefined ? [home_country_code] : rail_origins_payload,
      select_size,
      ...railFilterParams(rail),
      ...sourceFilterParams(rail),
      ...dateRangeParams(state.date_range),
    });
    return res?.data || [];
  },

  async fetchSignalTypes({ commit, state }) {
    if (Object.keys(state.signal_types_catalog).length) return state.signal_types_catalog;
    commit("SET_LOADING", { key: "signal_types", value: true });
    try {
      const res = await getExpansionSignalTypes({});
      const data = res?.data || res || [];
      commit("SET_SIGNAL_TYPES", data);
      return data;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return [];
      return [];
    } finally {
      commit("SET_LOADING", { key: "signal_types", value: false });
    }
  },

  // Fast movers: ahead_of_pace=true companies. Scope follows the map's current selection.
  async fetchFastMovers({ commit, state, rootState }) {
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) {
      commit("SET_FAST_MOVERS", []);
      return [];
    }
    const rail = state.rail_filters || {};
    const multi = (Array.isArray(state.selected_country_list) && state.selected_country_list.length)
      ? state.selected_country_list : undefined;
    commit("SET_FAST_MOVERS_LOADING", true);
    try {
      const res = await getExpansionLookup({
        profile_id,
        country_codes: multi,
        country_code:  multi ? undefined : (state.selected_country || undefined),
        is_global:     multi ? undefined : (state.selected_country ? undefined : true),
        direction:     state.flow_direction || "all",
        page:          1,
        per_page:      COMPANY_LIST_PER_PAGE,
        // Apply rail sub-filters so What's New is scoped like the main list.
        locations:     (Array.isArray(rail.origins) && rail.origins.length) ? rail.origins : undefined,
        ...railFilterParams(rail),
        ...sourceFilterParams(rail),
        // Fast movers are ahead-of-pace by definition; set after the spread to override the rail toggle.
        ahead_of_pace: true,
        ...dateRangeParams(state.date_range),
      });
      // /lookup response shape varies by build — `companies` is the canonical
      // key in newer builds, `data` is the legacy paginated wrapper, and a
      // raw array is what the bare lookup returns. Read whichever exists.
      const list = Array.isArray(res?.companies) ? res.companies
                : Array.isArray(res?.data)      ? res.data
                : Array.isArray(res)            ? res
                : [];
      commit("SET_FAST_MOVERS", list);
      return list;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return [];
      commit("SET_FAST_MOVERS", []);
      return [];
    } finally {
      commit("SET_FAST_MOVERS_LOADING", false);
    }
  },

  // Wide-expanders fetch. Window comes from the global date_range picker via
  // dateRangeParams — backend's buildTransitionWindow resolves date_from/date_to
  // /window_days the same way /dashboard does. No separate hardcoded window.
  async fetchWideExpanders({ commit, state, rootState }) {
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) { commit("SET_WIDE_EXPANDERS", []); return []; }
    const multi = (Array.isArray(state.selected_country_list) && state.selected_country_list.length)
      ? state.selected_country_list : undefined;
    commit("SET_WIDE_EXPANDERS_LOADING", true);
    try {
      const res = await getExpansionWideExpanders({
        profile_id,
        country_codes: multi,
        country_code:  multi ? undefined : (state.selected_country || undefined),
        direction:     state.flow_direction || "all",
        ...dateRangeParams(state.date_range),
      });
      commit("SET_WIDE_EXPANDERS", Array.isArray(res?.expanders) ? res.expanders : []);
      return res?.expanders || [];
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return [];
      commit("SET_WIDE_EXPANDERS", []);
      return [];
    } finally {
      commit("SET_WIDE_EXPANDERS_LOADING", false);
    }
  },

  // Saved-list activity fetch for the For-you tab. Same window/direction/
  // country params as the other dashboard reads — the backend cross-references
  // the caller's company groups against transitions in that window and
  // returns only lists with new movement.
  async fetchSavedListActivity({ commit, state, rootState }) {
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) { commit("SET_SAVED_LIST_ACTIVITY", []); return []; }
    const multi = (Array.isArray(state.selected_country_list) && state.selected_country_list.length)
      ? state.selected_country_list : undefined;
    commit("SET_SAVED_LIST_ACTIVITY_LOADING", true);
    try {
      const res = await getExpansionSavedListActivity({
        profile_id,
        country_codes: multi,
        country_code:  multi ? undefined : (state.selected_country || undefined),
        direction:     state.flow_direction || "all",
        ...dateRangeParams(state.date_range),
      });
      const lists = Array.isArray(res?.lists) ? res.lists : [];
      commit("SET_SAVED_LIST_ACTIVITY", lists);
      return lists;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return [];
      commit("SET_SAVED_LIST_ACTIVITY", []);
      return [];
    } finally {
      commit("SET_SAVED_LIST_ACTIVITY_LOADING", false);
    }
  },

  // Cross-market recent transitions for the global "Just moved" panel.
  // Backend enriches with company name + logo + home_country_code.
  async fetchRecentMovers({ commit, rootState }, { days = 30, select_size, limit } = {}) {
    commit("SET_RECENT_MOVERS_LOADING", true);
    try {
      const since_at = new Date(Date.now() - days * 86400000).toISOString();
      const res = await getExpansionRecent({
        profile_id: rootState.user.currentId.profile_id,
        since_at,
        // `select_size` is the API's standard list-size param; legacy `limit` still accepted by the backend + here.
        select_size: select_size != null ? select_size : (limit != null ? limit : 25),
      });
      const transitions = res?.transitions || [];
      commit("SET_RECENT_MOVERS", transitions);
      return transitions;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return [];
      return [];
    } finally {
      commit("SET_RECENT_MOVERS_LOADING", false);
    }
  },

  selectCountry({ commit, dispatch, state }, country_code) {
    // Cache check on (country) alone was returning stale country_detail when flow_direction/stage/date/rail changed between deselect + reselect — dropped; race guard in fetchCountryDetail handles staleness.
    const current_list = Array.isArray(state.selected_country_list) ? state.selected_country_list : [];
    const is_same_single = current_list.length === 1 && current_list[0] === country_code;
    if (!is_same_single) {
      commit("SET_COUNTRY_DETAIL", null);
    }
    return dispatch("setSelectedCountries", country_code ? [country_code] : null);
  },

  // Switch country + stage + map mode in one shot. Bypasses selectCountry's
  // dedup so a same-country/different-stage click still refetches.
  focusMarket({ commit, dispatch, state }, { country_code, stage_slug = null } = {}) {
    if (!country_code) return Promise.resolve(null);
    const next_stage = stage_slug == null ? null : stage_slug;
    const same_country = country_code === state.selected_country;
    const same_stage = next_stage === (state.selected_stage_slug || null);
    if (same_country && same_stage && state.map_mode === MAP_MODE.COUNTRY && state.country_detail) {
      return Promise.resolve(state.country_detail);
    }
    if (!same_country) commit("SET_COUNTRY_DETAIL", null);
    commit("SET_SELECTED_STAGE", next_stage);
    commit("SET_SELECTED_COUNTRY", country_code);
    commit("SET_COMPANY_PAGE", 1);
    commit("SET_MAP_MODE", MAP_MODE.COUNTRY);
    return dispatch("fetchCountryDetail", { country_code, stage_slug: next_stage });
  },

  selectStage({ commit, dispatch, state }, slug) {
    commit("SET_SELECTED_STAGE", slug);
    commit("SET_COMPANY_PAGE", 1);
    // Refetch both — dashboard so the map chip count narrows to the picked
    // stage, country detail so the company list filters server-side.
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", { country_code: state.selected_country, stage_slug: slug }),
    ]);
  },

  addClientCountry({ commit, dispatch, state }, country_code) {
    if (!country_code) return Promise.resolve();
    commit("ADD_CLIENT_COUNTRY", country_code);
    commit("SET_COMPANY_PAGE", 1);
    // Funnel every pick through the multi-list (the single source of truth) so
    // "Add market" reaches the URL and accumulates instead of replacing. Append
    // to the current scope — multi list, or the single slot as a 1-item set.
    const current = (Array.isArray(state.selected_country_list) && state.selected_country_list.length)
      ? state.selected_country_list
      : (state.selected_country ? [state.selected_country] : []);
    return dispatch("setSelectedCountries", [...current, country_code]);
  },

  removeClientCountry({ commit, state, dispatch, getters }, country_code) {
    commit("REMOVE_CLIENT_COUNTRY", country_code);
    // Drop from selected_country_list (source of truth) so the chip de-activates,
    // the map/list rescope, and the URL narrows — clearing to All Markets when the
    // last market goes. (Removing from client_added alone left it selected + in the URL.)
    const list = Array.isArray(state.selected_country_list) ? state.selected_country_list : [];
    if (list.includes(country_code)) {
      return dispatch("setSelectedCountries", list.filter((c) => c !== country_code));
    }
    // Fallback: code held only in the single slot (e.g. focusMarket) — pick the
    // next default so the dashboard doesn't stick on stale data (null → global).
    if (state.selected_country === country_code) {
      const next = getters.defaultCountryCode;
      commit("SET_SELECTED_COUNTRY", next);
      dispatch("fetchCountryDetail", { country_code: next || null });
    }
  },

  setMapMode({ commit }, mode) {
    commit("SET_MAP_MODE", mode);
  },

  // Flow direction is a slicer, not a scope — toggling it re-runs both dashboard + country detail with the new direction filter applied.
  setFlowDirection({ commit, dispatch }, dir) {
    commit("SET_FLOW_DIRECTION", dir);
    commit("SET_COMPANY_PAGE", 1);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
    ]);
  },

  // Multi-country selection. Empty/null = exit multi mode (revert to whatever
  // global/single state was). Re-fetches dashboard + country detail so map
  // chips and list scope simultaneously.
  setSelectedCountries({ commit, dispatch }, codes) {
    commit("SET_SELECTED_COUNTRY_LIST", codes);
    commit("SET_COMPANY_PAGE", 1);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
    ]);
  },

  clearMultiCountry({ commit, dispatch }) {
    commit("SET_SELECTED_COUNTRY_LIST", null);
    commit("SET_COMPANY_PAGE", 1);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
    ]);
  },

  toggleCountry({ state, dispatch }, code) {
    if (!code) return Promise.resolve();
    const upper = String(code).toUpperCase();
    const current = Array.isArray(state.selected_country_list) ? state.selected_country_list : [];
    const set = new Set(current);
    // If not in multi mode and there's a single selection, seed multi from it.
    if (current.length == 0 && state.selected_country) set.add(state.selected_country);
    set.has(upper) ? set.delete(upper) : set.add(upper);
    return dispatch("setSelectedCountries", [...set]);
  },

  // Single canonical reset for the date window. FiltersRail's Date chip ×
  // dispatches this; keeps the rail from re-deriving the default in two
  // places. Re-fetches detail + dashboard so all counts reflect the reset.
  resetDateRange({ commit, dispatch }) {
    commit("SET_DATE_RANGE", { from: null, to: null, window_days: DEFAULT_WINDOW_DAYS });
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
    ]);
  },

  // Coalesced reset used by FiltersRail's "Clear all" trash button. Without
  // this, clearAll fans out to 3-5 separate fetchCountryDetail dispatches:
  //   selectStage(null)        → fetchCountryDetail
  //   clearSelection()         → fetchCountryDetail (global)
  //   resetDateRange()         → fetchDashboard + fetchCountryDetail
  //   clear-source 'all' emit  → fetchCountryDetail (parent)
  // The race-guard inside fetchCountryDetail drops stale responses but
  // every dispatch still pays the network + DB cost. This action commits
  // all the state resets synchronously then fires ONE pair of refetches.
  bulkResetFilters({ commit, dispatch }) {
    // Single-action reset for "Clear all" — commits every piece of filter
    // state synchronously, then fires ONE fetchDashboard + fetchCountryDetail
    // pair. The previous clearAll() flow dispatched bulkResetFilters AND
    // clearMultiCountry sequentially, which fired 4 parallel fetches that
    // raced each other. The last-write-wins behaviour produced inconsistent
    // post-clear counts vs the fresh-load values (user-flagged).
    commit("SET_SELECTED_COUNTRY", null);
    commit("SET_SELECTED_COUNTRY_LIST", null);
    commit("SET_SELECTED_STAGE", null);
    commit("SET_FLOW_DIRECTION", "all");
    commit("SET_COMPANY_PAGE", 1);
    commit("SET_MAP_MODE", MAP_MODE.GLOBAL);
    commit("SET_DATE_RANGE", { from: null, to: null, window_days: DEFAULT_WINDOW_DAYS });
    commit("SET_RAIL_FILTERS", defaultRailFilters());
    // Drop the unscoped market snapshot so the next fetchDashboard repopulates
    // it from scratch — keeps stale picks-narrowed data from leaking into
    // chip rendering before the new response arrives.
    commit("SET_DASHBOARD_MARKET_INDEX", null);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", { country_code: null }),
    ]);
  },

  // Called by index.vue whenever the rail emits change. Commits the new shape
  // filters into Vuex (so fetchCountryDetail reads a single source of truth)
  // and refetches. Dedup-guarded: a same-shape emit (rail re-render with no
  // actual change) skips the refetch.
  applyRailFilters({ commit, dispatch, state }, filters) {
    const sortArr = (v) => (Array.isArray(v) ? [...v].sort() : []);
    const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
    const next = {
      directions:              sortArr(filters?.directions),
      freshness:               sortArr(filters?.freshness),
      scopes:                  sortArr(filters?.scopes),
      origins:                 sortArr(filters?.origins),
      vertical_categories:     sortArr(filters?.vertical_categories),
      vertical_sub_categories: sortArr(filters?.vertical_sub_categories),
      verticals:               sortArr(filters?.verticals),
      technologies:            sortArr(filters?.technologies),
      companies:               sortArr(filters?.companies),
      company_groups:          sortArr(filters?.company_groups),
      employee_slugs:          sortArr(filters?.employee_slugs),
      signal_strengths:        sortArr(filters?.signal_strengths),
      signal_type_slugs:       sortArr(filters?.signal_type_slugs),
      ahead_of_pace:    filters?.ahead_of_pace === true,
      only_contraction: filters?.only_contraction === true,
      employee_min: num(filters?.employee_min),
      employee_max: num(filters?.employee_max),
      founded_min:  num(filters?.founded_min),
      founded_max:  num(filters?.founded_max),
      revenue_min:  num(filters?.revenue_min),
      revenue_max:  num(filters?.revenue_max),
    };
    // Source filters (Jobs/Ads/News) — carried through rail_filters so the fetch actions forward them.
    // Stored order-preserved because date sub-fields are positional [start, end].
    const SOURCE_KEYS = ["job_titles","job_locations","job_exclude_locations","job_posted_dates","advertisement_target_locations","advertisement_exclude_target_locations","advertisement_search_terms","advertisement_start_dates","advertisement_end_dates","news_categories","news_published_dates","keywords"];
    SOURCE_KEYS.forEach((k) => { next[k] = Array.isArray(filters?.[k]) ? [...filters[k]] : []; });
    // filter_conditions = [{key, operator}] for the source filters' AND/OR toggles.
    next.filter_conditions = Array.isArray(filters?.filter_conditions) ? filters.filter_conditions.map((c) => ({ ...c })) : [];
    const cur = state.rail_filters || {};
    const ARRAY_KEYS = ["directions","freshness","scopes","origins","vertical_categories","vertical_sub_categories","verticals","technologies","companies","company_groups","employee_slugs","signal_strengths","signal_type_slugs"];
    const SCALAR_KEYS = ["ahead_of_pace","only_contraction","employee_min","employee_max","founded_min","founded_max","revenue_min","revenue_max"];
    const arraysSame = ARRAY_KEYS.every((k) => {
      const a = Array.isArray(cur[k]) ? [...cur[k]].sort() : [];
      return a.length === next[k].length && a.every((v, i) => v === next[k][i]);
    });
    const scalarsSame = SCALAR_KEYS.every((k) => (cur[k] == null ? null : cur[k]) === (next[k] == null ? null : next[k]));
    // Sorted compare just for change-detection (doesn't mutate next[k]); detects any add/remove/edit incl. date bounds.
    const sourceSame = SOURCE_KEYS.every((k) => {
      const a = Array.isArray(cur[k]) ? [...cur[k]].sort() : [];
      const b = [...next[k]].sort();
      return a.length === b.length && a.every((v, i) => v === b[i]);
    });
    // filter_conditions holds objects — JSON compare (order-insensitive by key) for change-detection.
    const fcKey = (fc) => JSON.stringify((Array.isArray(fc) ? fc : []).map((c) => [c.key, c.operator]).sort());
    const fcSame = fcKey(cur.filter_conditions) === fcKey(next.filter_conditions);
    if (arraysSame && scalarsSame && sourceSame && fcSame) return Promise.resolve(null);
    commit("SET_RAIL_FILTERS", next);
    commit("SET_COMPANY_PAGE", 1);
    // Dashboard + What's New refetch with the list so map count and fast-movers track the active filters.
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
      dispatch("fetchFastMovers").catch(() => {}),
    ]);
  },

  // Single "back to overview" action — clears the country + stage selection,
  // returns to global map mode, and immediately fetches the global aggregate
  // so the stage tiles + company list re-populate with master data instead of
  // showing zeros and the "Pick a country to begin" empty state.
  clearSelection({ commit, dispatch }) {
    commit("SET_SELECTED_COUNTRY", null);
    commit("SET_SELECTED_STAGE", null);
    commit("SET_COMPANY_PAGE", 1);
    commit("SET_MAP_MODE", MAP_MODE.GLOBAL);
    return dispatch("fetchCountryDetail", { country_code: null });
  },

  // Initial / on-demand global aggregate fetch — used when the dashboard
  // boots without a country in the URL so the master data renders right
  // away instead of forcing the user to pick a country first.
  fetchGlobalDetail({ dispatch }) {
    return dispatch("fetchCountryDetail", { country_code: null });
  },

  // Saved Views — backend = /saved/saved_searches (same one /search uses).
  // search_type_id for `expansion` is resolved at runtime so we don't hardcode
  // an id that differs across environments.
  resolveSavedViewsTypeId({ commit, state }) {
    if (state.saved_views.search_type_id) return Promise.resolve(state.saved_views.search_type_id);
    if (_saved_search_type_id_promise) return _saved_search_type_id_promise;
    _saved_search_type_id_promise = getSavedSearchTypes()
      .then((res) => {
        const rows = (res && (res.data || res)) || [];
        const expansion = rows.find((r) => r && r.slug === "expansion");
        const id = expansion ? expansion.search_type_id : null;
        if (id != null) commit("SET_SAVED_VIEWS_TYPE_ID", id);
        return id;
      })
      .catch(() => null)
      .finally(() => { _saved_search_type_id_promise = null; });
    return _saved_search_type_id_promise;
  },

  async fetchSavedViews({ commit, dispatch, state, rootState }, { force = false } = {}) {
    if (!force) {
      if (state.saved_views.views.length) return state.saved_views.views;
      if (state.saved_views.loading) return state.saved_views.views;
      if (state.saved_views.fetch_attempted && state.saved_views.search_type_id) return state.saved_views.views;
    }
    const id = await dispatch("resolveSavedViewsTypeId");
    const id_num = Number(id);
    if (!Number.isFinite(id_num) || id_num <= 0) {
      commit("SET_SAVED_VIEWS_FETCH_ATTEMPTED", true);
      return [];
    }
    // authorisationHandler reads `profile_id` from req.body to JOIN
    // Team_Profiles for ownership check — without it, auth fails before
    // body-validation. /search's getSaveList caller does the same.
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) {
      commit("SET_SAVED_VIEWS_FETCH_ATTEMPTED", true);
      return [];
    }
    commit("SET_SAVED_VIEWS_LOADING", true);
    try {
      const res = await getSaveList({ profile_id, search_type_id: id_num, order_by: "created_at", is_ascending_order: false });
      const list = (res && (res.data || res)) || [];
      commit("SET_SAVED_VIEWS", list);
      return list;
    } catch (e) {
      return [];
    } finally {
      commit("SET_SAVED_VIEWS_LOADING", false);
      commit("SET_SAVED_VIEWS_FETCH_ATTEMPTED", true);
    }
  },

  applySavedView({ commit, dispatch }, view) {
    if (!view) return Promise.resolve(null);
    const params = view.parameters || {};
    // Restore multi-market scope FIRST. SET_SELECTED_COUNTRY_LIST clears selected_country
    // via its mutation rule, so single-mode views (with selected_country set, list null)
    // restore correctly because the multi branch is skipped.
    if (Array.isArray(params.selected_country_list) && params.selected_country_list.length > 0) {
      commit("SET_SELECTED_COUNTRY_LIST", params.selected_country_list);
    } else if (params.selected_country !== undefined) {
      commit("SET_SELECTED_COUNTRY", params.selected_country || null);
    }
    if (params.selected_stage_slug !== undefined) {
      commit("SET_SELECTED_STAGE", params.selected_stage_slug || null);
    }
    if (params.map_mode) {
      commit("SET_MAP_MODE", params.map_mode);
    }
    if (params.date_range) {
      commit("SET_DATE_RANGE", params.date_range);
    }
    if (params.rail_filters) {
      commit("SET_RAIL_FILTERS", migrateLegacyRailFilters(params.rail_filters));
    }
    commit("SET_COMPANY_PAGE", 1);
    commit("SET_ACTIVE_VIEW_ID", view.saved_search_id);
    return dispatch("fetchCountryDetail", {});
  },

  // `pinned_sections` rides in the saved view so teammates loading it see
  // the same rail composition, not just the filter values.
  async createSavedView({ commit, dispatch, state, rootState }, { name, pinned_sections } = {}) {
    if (!name) return null;
    const id = await dispatch("resolveSavedViewsTypeId");
    if (!id) return null;
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) return null;
    const parameters = {
      selected_country:      state.selected_country,
      // Persist multi-market watchlist so restoring the view brings back the basket, not just a single country.
      selected_country_list: Array.isArray(state.selected_country_list) ? state.selected_country_list : null,
      selected_stage_slug:   state.selected_stage_slug,
      map_mode:              state.map_mode,
      date_range:            state.date_range,
      rail_filters:          state.rail_filters,
      pinned_sections:       Array.isArray(pinned_sections) ? pinned_sections : [],
    };
    try {
      const res = await addSaveList({
        profile_id,
        saved_search_name: name,
        parameters,
        search_type_id: id,
        is_pinned: false,
      });
      const view = res && res.data ? res.data : null;
      if (view) {
        // /create returns minimal row (no parameters echo). Hydrate locally
        // so the just-saved view applies cleanly without a refetch round-trip.
        commit("UPSERT_SAVED_VIEW", { ...view, parameters });
        commit("SET_ACTIVE_VIEW_ID", view.saved_search_id);
      }
      return view;
    } catch (e) {
      return null;
    }
  },

  async deleteSavedView({ commit, rootState }, id) {
    if (!id) return false;
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) return false;
    try {
      // Backend takes `saved_searches` (plural array), not `saved_search_id`.
      // profile_id required by authorisationHandler's Team_Profiles JOIN.
      await deleteSaveList({ profile_id, saved_searches: [id] });
      commit("REMOVE_SAVED_VIEW", id);
      return true;
    } catch (e) {
      return false;
    }
  },

  async renameSavedView({ commit, rootState }, { id, name }) {
    if (!id || !name) return false;
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) return false;
    try {
      const res = await updateSaveList({ profile_id, saved_searches: [id], saved_search_name: name });
      // /update returns an array of rows (parameterEnrichmentHandler output);
      // create returns a single row. Normalise.
      const rows = res && res.data;
      const updated = Array.isArray(rows) ? rows[0] : rows;
      if (updated) commit("UPSERT_SAVED_VIEW", { saved_search_id: id, name, ...updated });
      return true;
    } catch (e) {
      return false;
    }
  },

  clearActiveView({ commit }) {
    commit("SET_ACTIVE_VIEW_ID", null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
